import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import JeaButton from "@/components/jea/JeaButton";
import { useAuth } from "@/context/AuthContext";

const DEFAULT_IDLE_MS = 25 * 60 * 1000; // 25 minutes
const WARN_COUNTDOWN_MS = 5 * 60 * 1000; // 5 minutes

const SessionExpiryModal = () => {
  const { isAuthenticated, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(WARN_COUNTDOWN_MS / 1000);

  const idleTimerRef = useRef(null);
  const countdownRef = useRef(null);
  const idleMsRef = useRef(DEFAULT_IDLE_MS);
  // The activity-listener closure lives for the whole session, so it needs
  // a ref to observe `open` freshly (state closure would freeze at `false`).
  const openRef = useRef(false);

  // ── Idle-timeout override (dev/test override) ─────────────────────────
  useEffect(() => {
    if (typeof window === "undefined") return;
    let override = window.__JEA_IDLE_MS__;
    if (!override) {
      try {
        const ls = window.localStorage.getItem("jea_idle_ms");
        if (ls) override = Number(ls);
      } catch {}
    }
    if (!override) {
      try {
        const q = new URLSearchParams(window.location.search).get("jea_idle_ms");
        if (q) {
          override = Number(q);
          window.localStorage.setItem("jea_idle_ms", String(override));
        }
      } catch {}
    }
    if (override && Number.isFinite(override) && override > 0) {
      idleMsRef.current = Number(override);
    }
  }, []);

  // ── Timer helpers ─────────────────────────────────────────────────────
  const clearIdleTimer = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, []);
  const clearCountdown = useCallback(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  const doLogout = useCallback(async () => {
    clearIdleTimer();
    clearCountdown();
    openRef.current = false;
    setOpen(false);
    await logout();
    nav("/login", { replace: true });
  }, [logout, nav, clearIdleTimer, clearCountdown]);

  const startCountdown = useCallback(() => {
    clearIdleTimer();
    clearCountdown();
    openRef.current = true;
    setSecondsLeft(WARN_COUNTDOWN_MS / 1000);
    setOpen(true);
    // Delta-clock avoids drift + never freezes even if a tick is missed.
    const deadline = Date.now() + WARN_COUNTDOWN_MS;
    countdownRef.current = setInterval(() => {
      const remaining = Math.max(0, Math.round((deadline - Date.now()) / 1000));
      setSecondsLeft(remaining);
      if (remaining <= 0) {
        clearCountdown();
        doLogout();
      }
    }, 1000);
  }, [clearIdleTimer, clearCountdown, doLogout]);

  const scheduleIdle = useCallback(() => {
    clearIdleTimer();
    if (!isAuthenticated) return;
    idleTimerRef.current = setTimeout(() => startCountdown(), idleMsRef.current);
  }, [isAuthenticated, startCountdown, clearIdleTimer]);

  // ── Wire up activity listeners exactly once per auth session ──────────
  useEffect(() => {
    if (!isAuthenticated) {
      clearIdleTimer();
      clearCountdown();
      openRef.current = false;
      setOpen(false);
      return;
    }
    scheduleIdle();
    // NB: activity handler reads openRef so it stays fresh even after
    // startCountdown/stay flip the state. This is the fix for the
    // "countdown freezes at 4:58" bug where every mousemove was resetting
    // the idle timer + clearing the countdown interval.
    const onActivity = () => {
      if (openRef.current) return;
      scheduleIdle();
    };
    const events = ["mousemove", "keydown", "click", "touchstart", "scroll"];
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    return () => {
      events.forEach((e) => window.removeEventListener(e, onActivity));
      clearIdleTimer();
      clearCountdown();
    };
  }, [isAuthenticated, scheduleIdle, clearIdleTimer, clearCountdown]);

  // ── Modal actions ────────────────────────────────────────────────────
  const stay = () => {
    clearCountdown();
    openRef.current = false;
    setOpen(false);
    // Give the user a fresh full idle window instead of the leftover.
    scheduleIdle();
  };

  const mm = String(Math.floor(secondsLeft / 60)).padStart(2, "0");
  const ss = String(secondsLeft % 60).padStart(2, "0");

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 bg-black/60 backdrop-blur-[12px] z-[200]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
          <motion.div
            role="alertdialog"
            aria-modal="true"
            className="fixed inset-0 z-[201] flex items-center justify-center px-4"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.2 }}
            data-testid="session-expiry-modal"
          >
            <div className="w-full max-w-[440px] bg-bg-secondary border border-warning/40 rounded-[16px] p-8 shadow-elevated">
              <div className="flex items-start gap-3 mb-4">
                <div className="mt-0.5 h-9 w-9 rounded-full bg-warning/15 border border-warning/40 flex items-center justify-center">
                  <AlertTriangle size={16} className="text-warning" />
                </div>
                <div>
                  <div className="text-white font-bold text-[16px]">You've been inactive</div>
                  <div className="text-text-secondary text-[13px]">
                    For your security, we'll sign you out shortly. Click "Stay Logged In" to
                    continue.
                  </div>
                </div>
              </div>
              <div
                className="jea-mono text-center text-[28px] font-bold text-warning mb-6"
                data-testid="session-expiry-countdown"
              >
                {mm}:{ss}
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={doLogout}
                  className="flex-1 h-11 rounded-[8px] border border-border-default text-text-secondary hover:text-white hover:bg-bg-tertiary transition-colors text-[14px] font-semibold"
                  data-testid="session-expiry-logout"
                >
                  Log Out
                </button>
                <JeaButton onClick={stay} className="flex-1" data-testid="session-expiry-stay">
                  Stay Logged In
                </JeaButton>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SessionExpiryModal;
